import { useState } from 'react';
import { ProgressBar } from '@app/components/shared/ProgressBar';
import { buttonStyles, cardStyles, pageStyles } from '@/styles/sharedStyles';

interface Step3Props {
  onNext: (data: any) => void;
  onBack?: () => void;
  onStepClick?: (step: number) => void;
  stepData?: any;
  flowType?: 'one-off' | 'subscription';
}

export function Step3({ onNext, onStepClick }: Step3Props) {
  const [selectedOption, setSelectedOption] = useState('online');

  // Validation logic (selectedOption should always be set, but checking for safety)
  const isFormValid = selectedOption !== '';

  const handleNext = () => {
    if (!isFormValid) return;
    onNext({ setupMethod: selectedOption });
  };

  return (
    <div className="max-w-7xl w-full mx-auto">
      <ProgressBar 
        currentStep={3} 
        totalSteps={4} 
        onStepClick={onStepClick}
      />

      <div className="max-w-3xl w-full mx-auto">
        <div className="text-center mb-8">
          <h1 className={pageStyles.title}>Choose how to complete your service setup</h1>
          <p className={pageStyles.subtitle}>
            Select how you'd like to provide the details we need to create personalized gift recommendations for your recipients.
          </p>
        </div>

        <div className="space-y-6 mb-8">
          <div 
            className={`${cardStyles.base} cursor-pointer transition-all duration-300 ${
              selectedOption === 'online' 
                ? 'border-2 border-purple-medium shadow-md' 
                : 'border border-gray-200 hover:border-purple-light hover:shadow-sm'
            }`}
            onClick={() => setSelectedOption('online')}
          >
            <div className="flex items-start gap-4">
              <div className="mt-1">
                <input 
                  type="radio" 
                  checked={selectedOption === 'online'} 
                  onChange={() => setSelectedOption('online')}
                  className="w-5 h-5 text-purple-medium focus:ring-purple-medium"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Complete your recipient details online</h3>
                <p className="text-gray-600">
                  Fill out a detailed form with information about your recipients, their interests, preferences, and sizing. This option gives you full control over the details.
                </p>
              </div>
            </div>
          </div>

          <div 
            className={`${cardStyles.base} cursor-pointer transition-all duration-300 ${
              selectedOption === 'call' 
                ? 'border-2 border-purple-medium shadow-md' 
                : 'border border-gray-200 hover:border-purple-light hover:shadow-sm'
            }`}
            onClick={() => setSelectedOption('call')}
          >
            <div className="flex items-start gap-4">
              <div className="mt-1">
                <input 
                  type="radio" 
                  checked={selectedOption === 'call'} 
                  onChange={() => setSelectedOption('call')}
                  className="w-5 h-5 text-purple-medium focus:ring-purple-medium"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Prefer to discuss your needs? Schedule a quick call</h3>
                <p className="text-gray-600">
                  Have a personalized conversation with our gift concierge team. We'll ask thoughtful questions to understand your recipients and create perfect recommendations.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button 
            className={isFormValid ? buttonStyles.primary : buttonStyles.disabled}
            onClick={handleNext}
            disabled={!isFormValid}
          >
            NEXT →
          </button>
        </div>
      </div>
    </div>
  );
}